type LoadFacebookSdkOptions = {
  appId: string
  version: string
}

const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk'
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js'
const FACEBOOK_SDK_LOAD_TIMEOUT_MS = 10_000

let facebookSdkPromise: Promise<FacebookSDK> | null = null

export function loadFacebookSdk({ appId, version }: LoadFacebookSdkOptions): Promise<FacebookSDK> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Facebook SDK is only available in the browser'))
  }

  if (window.FB) {
    const sdk = window.FB
    sdk.init({ appId, cookie: true, xfbml: false, version })
    return Promise.resolve(sdk)
  }

  if (facebookSdkPromise) {
    return facebookSdkPromise
  }

  facebookSdkPromise = new Promise<FacebookSDK>((resolve, reject) => {
    const finish = (callback: () => void) => {
      if (settled) return;

      settled = true
      window.clearTimeout(timeoutId)
      callback()
    }
    let settled = false
    const timeoutId = window.setTimeout(() => {
      finish(() => {
        facebookSdkPromise = null
        reject(new Error('Facebook SDK timed out while loading'))
      })
    }, FACEBOOK_SDK_LOAD_TIMEOUT_MS)

    window.fbAsyncInit = () => {
      const sdk = window.FB
      if (!sdk) {
        finish(() => {
          facebookSdkPromise = null
          reject(new Error('Facebook SDK failed to initialize'))
        })
        return
      }

      sdk.init({ appId, cookie: true, xfbml: false, version })
      finish(() => resolve(sdk))
    }

    const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID)
    if (existingScript) {
      existingScript.remove()
    }

    const script = document.createElement('script')
    script.id = FACEBOOK_SDK_SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = FACEBOOK_SDK_SRC
    script.onerror = () => {
      finish(() => {
        facebookSdkPromise = null
        reject(new Error('Facebook SDK failed to load'))
      })
    }

    ;(document.body || document.head || document.documentElement).appendChild(script)
  })

  return facebookSdkPromise
}
